import {
  HIGH_LEVEL_VALUES_TO_LABELS,
  METRIC_VALUES_TO_LABELS,
} from 'constants/metricValuesToLabels';
import {
  BRAND_AFFINITY_DISPLAY,
  BRAND_AFFINITY_FIELD,
} from 'modules/journey/components/FilterInputsList/FilterInputsList.constants';
import {
  Cell,
  RightAlignText,
  TabularNumberFont,
  TooltipWrapper,
} from 'modules/productAnalytics/Styled';
import { formatNumberGivenValueType, metricSentenceCase } from 'modules/productAnalytics/utils';
import moment, { Moment } from 'moment';
import React from 'react';
import { CURRENCY, PERCENTAGE } from 'ui/card/constants';
import { TEST_1_DECIMAL, formatPercentage } from 'ui/card/utils';
import { CUSTOM_RANGE, RELATIVE_DATE_PRESETS } from 'ui/datePicker/helpers';
import { Skeleton } from 'ui/skeleton';
import { AlignTypes } from 'ui/tableHeader';
import { Tooltip } from 'ui/tooltip';
import { isDev } from 'utils/env';
import { URL_LIMITS } from 'utils/urlParams';

import { AllMessageMedium } from 'modules/domains/campaign';
import { ChildrenCell, SegCell } from './Styled';
import { Segment } from './components/Audience/types';
import { MetricDefinitionWrapper } from './components/MetricDefinitionWrapper';
import {
  BreakdownToInApp,
  CampaignToHighLevelMetric,
  InAppBreakdownToAggregate,
  LowerCaseMediumOptions,
  MediumOptions,
} from './constants';
import {
  AllFilters,
  Campaign,
  CampaignData,
  CampaignLevelMetrics,
  ColumnData,
  DateParams,
  InAppDestination,
  IsoString,
  Metric,
  MetricOption,
  Preset,
  SavedView,
  SelectedMetrics,
  SetDateParams,
} from './types';

/**
 * Get a mapping of available campaigns to their number of occurrences
 * {Email: 4, InApp: 5, Push: 2}
 */
export const getCampaignMediumsByFrequency = (campaigns: Campaign[]) =>
  campaigns.reduce<Record<string, number>>((sums, { medium }: Campaign) => {
    sums[medium] = sums[medium] ? sums[medium] + 1 : 1;
    return sums;
  }, {});

export const sanitizeMediumParam = (medium: string) => medium.replace('-', '').replace(' ', '');

/**
 * @param mediums
 * @returns Email, Push and Send (user-friendly listing of mediums)
 */
export const getNiceMediumList = (mediums: string[]) => {
  return mediums.reduce((acc: string, medium: string, idx: number) => {
    if (idx === 0) {
      return MediumOptions[medium];
    }
    if (idx === mediums.length - 1) {
      return `${acc} and ${MediumOptions[medium]}`;
    }
    return `${acc}, ${MediumOptions[medium]}`;
  }, '');
};

/**
 * @param metricName name to convert (e.g. TotalEmailSend)
 * @returns User-friendly metric label (e.g. TotalEmailSend => Total Email Sends)
 */
export const getUserFriendlyMetricLabel = (metricName: string) =>
  HIGH_LEVEL_VALUES_TO_LABELS[metricName] || METRIC_VALUES_TO_LABELS[metricName] || metricName;

/**
 * @param metricLabel a single-medium metric name (e.g. 'Unique Email Sends')
 * @returns the name of the medium (Email, Push, etc.) for a given metric
 * (e.g.) given the push metric ('Unique Push Sends'), this fn would return 'Push'
 */
export const getMetricMedium = (metricLabel: string, analyticsMetrics: Metric[]) => {
  const metric = analyticsMetrics.find((met: Metric) => met.metricNiceName === metricLabel);
  return metric?.mediums.length === 1 ? metric?.mediums[0] : 'Other';
};

export const getCampaignTotalText = (
  numCampaignsPerMedium: Record<string, number>,
  totalCampaigns: number,
) => {
  if (Object.keys(numCampaignsPerMedium).length === 1) {
    const [[medium, num]] = Object.entries(numCampaignsPerMedium);
    const pluralSuffix = ['WebPush', 'Push'].includes(medium) ? 'es' : 's';
    return `${num} ${MediumOptions[medium]}${num === 1 ? '' : pluralSuffix}`;
  }
  const campaignMediumBreakdownText = Object.entries(numCampaignsPerMedium)
    .map(([medium, num]) => `${MediumOptions[medium]} (${num})`)
    .join(', ');
  return `${totalCampaigns} campaign${
    totalCampaigns === 1 ? '' : 's'
  }: ${campaignMediumBreakdownText}`;
};

/**
 * Process raw campaign metric data, so that listed values aren't wrapped in an extra object
 * E.g. { eventName: { value: 8 }} => { eventName: 8 }
 */
export const getProcessedMetricsForCampaign = (metricsForCampaignsResp: CampaignLevelMetrics) =>
  metricsForCampaignsResp.map((el) => {
    const { mediumMetrics, overallMetrics } = el;
    const processedOverallMetrics: { [eventName: string]: number } = {};
    Object.keys(overallMetrics).forEach((key) => {
      processedOverallMetrics[key] = overallMetrics[key].value;
    });
    const processedMediumMetrics: { [eventName: string]: number } = {};
    const processedDestinationBreakdownMetrics: {
      [eventName: string]: InAppDestination[] | undefined;
    } = {};
    Object.keys(mediumMetrics).forEach((key) => {
      if (BreakdownToInApp[key]) {
        processedDestinationBreakdownMetrics[key] =
          // @ts-ignore
          mediumMetrics[key].value?.destinations;
      } else {
        // @ts-ignore
        processedMediumMetrics[key] = mediumMetrics[key].value;
      }
    });
    return {
      ...el,
      mediumMetrics: processedMediumMetrics,
      overallMetrics: processedOverallMetrics,
      breakdownMetrics: processedDestinationBreakdownMetrics,
    };
  });

export const paginate = (
  arr: any[],
  pageSize: number,
  pageNumber: number,
  setPageVals: React.Dispatch<
    React.SetStateAction<{
      pageSize: number;
      currentPage: number;
    }>
  >,
) => {
  const startPoint = (pageNumber - 1) * pageSize;
  const endPoint = Math.min(arr.length, pageNumber * pageSize);

  if (startPoint >= endPoint && pageNumber !== 1) {
    setPageVals({ pageSize, currentPage: 1 });
    return arr.slice(0, endPoint);
  }
  return arr.slice(startPoint, endPoint);
};

// Currently excluding Embedded as a medium
const generateDoubleLine = (mediums: string[]) => {
  const mediumLabels = mediums.reduce((labels, med) => {
    const label = MediumOptions[med];
    return label ? [...labels, label] : labels;
  }, [] as string[]);
  return mediumLabels.length === 5 ? 'All' : mediumLabels.join(', ');
};

const optionalizeMetric = (selectedMediums: string[] = [], metric: Metric) => {
  return {
    categoryId: metric.category?.toLowerCase(),
    label: metric.metricNiceName,
    id: metric.metricName,
    value: metric.metricName,
    valueType: metric.valueType,
    ...(selectedMediums.length > 1 ? { doubleLine: generateDoubleLine(metric.mediums) } : {}),
  };
};

/**
 * Filter metric options displayed in Filter Side Panel based on the selected mediums
 */
export const filterMetricsByMedium = (
  selectedMediums: string[] = [],
  metrics: Metric[],
): Metric[] =>
  metrics?.filter(
    (metric) => !!metric.category && metric.mediums.some((med) => selectedMediums.includes(med)),
  );

/**
 * Filters out specified mediums from the metrics.
 * @param {Metric[]} metrics - The array of metrics.
 * @param {AllMessageMedium[]} excludedMediums - The mediums to exclude.
 * @returns {Metric[]} - The filtered array of metrics.
 */
export const filterMediumsFromMetrics = (
  metrics: Metric[],
  excludedMediums: AllMessageMedium[],
): Metric[] => {
  return metrics.map((metric) => ({
    ...metric,
    mediums: metric.mediums.filter(
      (medium) => !excludedMediums.includes(medium as AllMessageMedium),
    ),
  }));
};

export const createMetricOptions = (
  selectedMediums: string[] = [],
  metrics: Metric[],
  excludedMediums: AllMessageMedium[],
): MetricOption[] => {
  const filteredMetrics = filterMediumsFromMetrics(metrics, excludedMediums);
  return filteredMetrics.map((metric) => optionalizeMetric(selectedMediums, metric));
};

/**
 * Filters out selected metrics that do not correspond to the selected mediums
 */
export const filterSelectedMetricsByMedium = (
  mediums: string[] = [],
  selectedMetrics: string[] = [],
  highLevelMetrics: Metric[],
  analyticsMetrics: Metric[],
) => {
  const filteredMetrics = filterMetricsByMedium(mediums, [
    ...analyticsMetrics,
    ...highLevelMetrics,
  ]);
  const metricsList = filteredMetrics.map((met) => met.metricName);
  return selectedMetrics.filter((metric) => metricsList.includes(metric));
};

export const formatBarChartNum = (valueType: string, value?: number) => {
  let formattedNumber;
  let abbreviatedNumber;
  if (typeof value !== 'number') {
    return '--';
  } else {
    formattedNumber = value.toLocaleString();
  }
  if (valueType === PERCENTAGE) {
    formattedNumber = formatPercentage(value);
  } else {
    const number = Number(value);
    if (TEST_1_DECIMAL.test(`${number}`) && valueType !== CURRENCY) {
      abbreviatedNumber = Number(number.toFixed(0)).toLocaleString();
    }
  }
  if (valueType === CURRENCY) {
    if (abbreviatedNumber) {
      abbreviatedNumber = `$${abbreviatedNumber}`;
    }
    formattedNumber = `$${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  if (abbreviatedNumber) {
    return abbreviatedNumber;
  }
  return formattedNumber;
};
// eslint-disable-next-line func-names
export const stringifyArr = function <T>(arr: T[]) {
  return arr.sort().toString();
};

/**
 * @param selectedFilters saved filters from the old single-medium MI exp
 * @returns filters object {[filterName]: [filterValue]}
 */
export const parseSelectedFilters = (selectedFilters: string[]) => {
  const parsedFilters: Record<string, string | string[]> = {};
  selectedFilters.forEach((filter) => {
    const [key, val] = filter.split('-');
    parsedFilters[key] = parsedFilters[key] ? [...parsedFilters[key], val] : [val];
  });
  return parsedFilters;
};

const selectedMetricsReducer = (acc: SelectedMetrics, curr: string) => {
  acc.push({ name: curr });
  return acc;
};

export const parseSelectedMetrics = (selectedMetrics: string[]) => {
  const parsedMetrics: SelectedMetrics = [];
  selectedMetrics.reduce(selectedMetricsReducer, parsedMetrics);
  return parsedMetrics;
};

/**
 * @param selectedMets selected metrics from the old single-medium MI
 * @param selectedCrossMets selected metrics (mix of single-medium and high-level) from the multi-medium experience
 * @returns cross/HLM translations of selectedMets, if no selectedCrossMets exist
 */
export const translateMediumToHighLevelMetrics = (
  selectedMets = [] as string[],
  selectedCrossMets?: string[],
) => {
  if (selectedCrossMets) {
    return selectedCrossMets;
  }
  return selectedMets.map((metric) => {
    if (CampaignToHighLevelMetric[metric]) {
      return CampaignToHighLevelMetric[metric];
    }
    return metric;
  });
};

/**
 * @param selectedCrossMetrics
 * @returns selectedCrossMetrics split into overallMetrics + mediumMetrics
 */
export const getHighLevelAndMedMetricsFromMetrics = (
  selectedCrossMetrics: string[] = [],
  highLevelMetrics: Metric[],
  analyticsMetrics: Metric[],
) => {
  const parsedSelectedMetrics = parseSelectedMetrics(selectedCrossMetrics);
  const highLevelMetricsSet = new Set(highLevelMetrics.map((el) => el.metricName));
  const analyticsMetricsSet = new Set(analyticsMetrics.map((el) => el.metricName));
  const overallMetrics = parsedSelectedMetrics
    .filter((val: any) => highLevelMetricsSet.has(val.name))
    .map((val: any) => ({ enumName: val.name }));
  const mediumMetrics = parsedSelectedMetrics.filter((val: any) =>
    analyticsMetricsSet.has(val.name),
  );
  return { overallMetrics, mediumMetrics };
};

/**
 * @param selectedCrossMetrics
 * @returns selectedCrossMetrics split into overallMetrics + mediumMetrics
 */
export const getHighLevelAndMedMetrics = (
  selectedCrossMetrics: string[] = [],
  highLevelMetrics: Metric[],
  analyticsMetrics: Metric[],
) => {
  const highLevelMetricsSet = new Set(highLevelMetrics.map((el) => el.metricName));
  const analyticsMetricsSet = new Set(analyticsMetrics.map((el) => el.metricName));
  const overallMetrics = selectedCrossMetrics
    .filter((val: any) => highLevelMetricsSet.has(val))
    .map((val: any) => ({ enumName: val }));
  const mediumMetrics = selectedCrossMetrics
    .filter((val: string) => analyticsMetricsSet.has(val))
    .map((val: any) => ({ name: val }));
  return { overallMetrics, mediumMetrics };
};

/**
 * given an object mapping url params to values, piece together the
 * final url while ensuring its length does not pass the character limit
 * size limits: 4096 (local), 8192 bytes (staging + prod)
 */
export const sharedUrl = (queryParams: { [param: string]: string }) => {
  const charLimit = isDev() ? URL_LIMITS.DEV : URL_LIMITS.PROD;
  const { origin, pathname } = window.location;
  let baseUrl = `${origin}${pathname}?`;
  if (pathname.startsWith('/messagingInsights') || pathname.startsWith('/savedViews')) {
    baseUrl = `${origin}/messagingInsights?`;
  }
  let urlLength = baseUrl.length;
  const error: string[] = [];
  const queryString = Object.entries(queryParams)
    ?.sort((a, b) => a[1]?.length - b[1]?.length) // move longest values to the end of the url
    ?.map(([name, value], i) => {
      const query = `${name}=${value}`;
      urlLength = urlLength + query.length + (i > 0 ? 1 : 0);
      if (urlLength > charLimit) {
        error.push(name);
        return '';
      }
      return value?.length > 0 ? query : '';
    })
    ?.filter((q) => !!q)
    ?.join('&');
  const completeUrl = `${baseUrl}${queryString}`;
  return { completeUrl, error };
};

export const compileSharedUrl = (allFilters: AllFilters) => {
  const {
    selectedMediums,
    selectedCrossMetrics,
    campaignFilters,
    dateParams,
    timezone,
    selectedTab,
    selectedSegment,
    region,
    campaignsSortDirection,
    campaignsSortedBy,
    audienceSortDirection,
    audienceSortedBy,
    snippetsSortDirection,
    snippetsSortedBy,
  } = allFilters;

  let queryParams: { [param: string]: string } = {};

  // parse selected mediums and metrics
  if (selectedMediums && selectedMediums.length > 0) {
    queryParams.messageMediums = selectedMediums.join(',');
  }
  if (selectedCrossMetrics && selectedCrossMetrics.length > 0) {
    queryParams.metrics = selectedCrossMetrics.join(',');
  }

  // parse selected times, timezone
  const { startTime, endTime } = dateParams;
  queryParams = { ...queryParams, timezone };

  queryParams.startTime = startTime?.toISOString() || '';
  queryParams.endTime = endTime?.toISOString() || '';

  // parse campaignFilters (i.e. filters used in the Filter Builder)
  Object.entries(campaignFilters || {}).forEach(([optName, val]) => {
    queryParams[optName] = [...val].join(',');
  });

  // parse selected tab
  queryParams.tab = selectedTab;

  // parse segments
  if (selectedSegment?.name) {
    queryParams.segment = selectedSegment.name;
  }

  // parse selected region (used in the locations tab)
  if (selectedMediums && selectedMediums.includes(MediumOptions.Email)) {
    const [selectedRegion] = region;
    queryParams.region = selectedRegion;
  }

  // parse table sort info
  queryParams.campaignsSortBy = campaignsSortedBy;
  queryParams.campaignsSortDir = campaignsSortDirection;
  queryParams.audienceSortBy = audienceSortedBy;
  queryParams.audienceSortDir = audienceSortDirection;
  if (snippetsSortedBy) {
    queryParams.snippetsSortBy = snippetsSortedBy;
  }
  if (snippetsSortDirection) {
    queryParams.snippetsSortDir = snippetsSortDirection;
  }

  return sharedUrl(queryParams);
};

export const applyQueryTimePresetHelper = (
  preset: string,
  startTime: IsoString,
  setDateParams: SetDateParams,
  setTimePreset: (timePreset: Preset) => void,
  tzParam: string,
  currTimezone: string,
) => {
  // @ts-ignore
  const relativePreset = RELATIVE_DATE_PRESETS[preset];
  if (!startTime && preset !== CUSTOM_RANGE && relativePreset?.format) {
    // @ts-ignore
    const { startDate, endDate } = relativePreset.format(tzParam || currTimezone);
    setDateParams({
      startTime: startDate,
      endTime: endDate,
    });
    setTimePreset(preset as Preset);
  }
};

export const convertQueryParamsToString = (queryParams: any) => {
  return Object.entries(queryParams)
    ?.map(([name, value]) => {
      const query = `${name}=${value}`;
      return value ? query : '';
    })
    ?.filter((q) => !!q)
    ?.join('&');
};

export const getViewIdsQueryString = (recentlyViewedIds: string[]) => {
  if (recentlyViewedIds.length < 1) {
    return 'pageSize=5&page=1';
  }
  const idsString = recentlyViewedIds.reduce((acc, id) => {
    return `${acc}ids=${id}&`;
  }, '');
  return `${idsString}pageSize=${recentlyViewedIds.length}&page=1`;
};

/**
 *
 * @param key id
 * @param sortedArray correct id order [1,2,3]
 * @param objArray [{id: 2}, {id: 1}, {id: 3}]
 * @returns objArray sorted by the keys listed in sortedArray [{id: 1}, {id: 2}, {id: 3}]
 */
export const sortObjectArrayByKeyArray = (
  key: string,
  sortedArray: any[],
  objArray: Record<string, any>[],
) =>
  sortedArray.reduce(
    (acc, i) => {
      const match = objArray.find((j) => j[key] === i);
      return match ? [...acc, match] : acc;
    },
    [] as Record<string, any>[],
  );

export const sortViewsByUpdatedAt = (a: SavedView, b: SavedView) =>
  b.updatedAt > a.updatedAt ? 1 : -1;

export const parseField = (field: Segment = {}) => {
  if (field.name === BRAND_AFFINITY_FIELD) {
    return {
      ...field,
      name: BRAND_AFFINITY_DISPLAY,
    };
  }
  return field;
};

export const getMetricColumns = (
  selectedMetrics: string[],
  dateParams: DateParams,
  ValueTypes: Record<string, string>,
) => {
  const metricCols: ColumnData[] = [];
  selectedMetrics.forEach((val) => {
    metricCols.push({
      name: (
        <MetricDefinitionWrapper metricName={val}>
          {metricSentenceCase(val)}
        </MetricDefinitionWrapper>
      ),
      value: val,
      align: 'flex-end',
      RowCell: ({ row }) => {
        const areCampaignMetricsOutOfTimeRange =
          row.campaignType === 'Blast' &&
          row.launchDate &&
          moment(row.launchDate) < (dateParams.startTime as Moment);
        const cellValue = formatNumberGivenValueType(ValueTypes[val], row[val]);
        if (areCampaignMetricsOutOfTimeRange && typeof row[val] === 'number' && row[val] <= 0) {
          return (
            <Cell>
              <TooltipWrapper>
                <Tooltip
                  tooltipContent={`This campaign’s send time is outside of your selected date range. Adjust your date range filter to see this campaign’s ${val}`}
                >
                  <RightAlignText>
                    <TabularNumberFont>{row[val] < 0 ? 'N/A' : cellValue}</TabularNumberFont>
                  </RightAlignText>
                </Tooltip>
              </TooltipWrapper>
            </Cell>
          );
        }
        return (
          <Cell>
            <RightAlignText>
              <TabularNumberFont>{cellValue}</TabularNumberFont>
            </RightAlignText>
          </Cell>
        );
      },
      ChildrenCell: ({ row }) => (
        <ChildrenCell>
          <RightAlignText>
            <TabularNumberFont>
              {formatNumberGivenValueType(ValueTypes[val], row[val])}
            </TabularNumberFont>
          </RightAlignText>
        </ChildrenCell>
      ),
    });
  });
  return metricCols;
};

export const getMetricColumnsForSegment = (
  selectedMetrics: string[],
  ValueTypes: Record<string, string>,
) => {
  const metricCols: ColumnData[] = [];
  selectedMetrics.forEach((val) => {
    metricCols.push({
      name: (
        <MetricDefinitionWrapper metricName={val} inSegmentAnalysis>
          {metricSentenceCase(val)}
        </MetricDefinitionWrapper>
      ),
      value: val,
      align: 'flex-end' as AlignTypes,
      RowCell: ({ row }) => (
        <SegCell metric>
          <RightAlignText>
            <TabularNumberFont>
              {row.medium === 'All' ? '' : formatNumberGivenValueType(ValueTypes[val], row[val])}
            </TabularNumberFont>
          </RightAlignText>
        </SegCell>
      ),
    });
  });
  return metricCols;
};

export const parseCampaignMetricData = (
  campaignMetricsData: Campaign[] = [],
  showInAppDestinations = false,
) =>
  campaignMetricsData.map((campaign) => {
    const campaignData: CampaignData = {
      ...campaign,
      ...campaign.metrics,
    };
    delete campaignData.metrics;
    delete campaignData.breakdownMetrics;
    const inAppMetricsUsed: Set<string> = new Set();
    if (showInAppDestinations && campaign.metrics?.breakdownMetrics) {
      const metricsByDestination: Record<string, Record<string, string>> = {};
      const others = {
        appName: 'Others',
        appPackageName: 'Others',
        platform: '',
      };
      Object.keys(campaign.metrics.breakdownMetrics).forEach((metric) => {
        campaign?.metrics?.breakdownMetrics[metric].forEach(
          (destination: Record<string, string>) => {
            let key;
            if (!destination.appName || !destination.appPackageName) {
              key = 'Others';
              if (!metricsByDestination[key]) {
                metricsByDestination[key] = { ...others };
              }
            } else {
              key = destination.appPackageName;
              if (!metricsByDestination[key]) {
                metricsByDestination[key] = { ...destination };
                delete metricsByDestination[key].value;
              }
            }

            const mediumMetric = BreakdownToInApp[metric];
            const aggregateMetric = InAppBreakdownToAggregate[metric];
            const availableMetricNames = Object.keys(campaign.metrics || {});
            if (key === 'Others') {
              metricsByDestination[key][mediumMetric] = `${
                parseInt(metricsByDestination[key][mediumMetric] || '0', 10) +
                parseInt(destination.value, 10)
              }`;
              inAppMetricsUsed.add(mediumMetric);
              if (aggregateMetric && availableMetricNames.includes(aggregateMetric)) {
                metricsByDestination[key][aggregateMetric] = `${
                  parseInt(metricsByDestination[key][aggregateMetric] || '0', 10) +
                  parseInt(destination.value, 10)
                }`;
                inAppMetricsUsed.add(aggregateMetric);
              }
            } else {
              metricsByDestination[key][mediumMetric] = destination.value;
              inAppMetricsUsed.add(mediumMetric);
              if (aggregateMetric && availableMetricNames.includes(aggregateMetric)) {
                metricsByDestination[key][aggregateMetric] = destination.value;
                inAppMetricsUsed.add(aggregateMetric);
              }
            }
          },
        );
      });
      /** Below we are making sure to show 0 for the breakdown metrics with no event data instead of N/A
       * First we are collecting the metrics used in an array and then making sure to have default values
       * for each metric for each destination.
       */
      const defaultInAppMetricsUsed = [...inAppMetricsUsed].reduce(
        (metricsObject, metric) => ({
          ...metricsObject,
          [metric]: 0,
        }),
        {},
      );
      const metricByDestinationValues = Object.values(metricsByDestination).map((values) => ({
        ...defaultInAppMetricsUsed,
        ...values,
      }));
      campaignData.childrenRows = metricByDestinationValues;
    }
    return campaignData;
  });

export const addExpandStateToCampaignMetricData = (
  campaignMetricsData: Campaign[],
  expandState: Record<number, boolean>,
) =>
  campaignMetricsData.map((campaignData) => ({
    ...campaignData,
    isExpanded: expandState[campaignData.campaignId],
  }));

export const parseLabelsFromSelectedMetrics = (selectedMetrics: string[] = []) =>
  selectedMetrics.map(
    (metric) => METRIC_VALUES_TO_LABELS[metric] || HIGH_LEVEL_VALUES_TO_LABELS[metric],
  );

export const getLoadingRows = (metrics: string[], numberOfRows: number) => {
  const skeletonRows = [];
  for (let i = 0; i < numberOfRows; i += 1) {
    const row: Record<string, any> = { id: `skeleton-row-${i}` };
    metrics.forEach((metric) => {
      row[metric] = <Skeleton isRound height={17} width={metric === 'campaignName' ? 230 : 69} />;
    });
    skeletonRows.push(row);
  }
  return skeletonRows;
};

export const getPartialLoadingRows = (rows: Record<string, any>[], metrics: string[]) =>
  rows.map((row) => {
    const skeletonRow: Record<string, any> = {};
    metrics.forEach((metric) => {
      skeletonRow[metric] = <Skeleton isRound height={17} width={69} />;
    });
    return { ...row, ...skeletonRow };
  });

/**
 * Removes whitespace if any. Makes the medium text case insensitive and filter outs the malformed params.
 */
export const cleanUrlParams = (params: string[] | undefined) =>
  params
    ?.map((param: string) => LowerCaseMediumOptions[param.trim().toLocaleLowerCase()])
    ?.filter((param: string) => !!param);

/**
 * Removes whitespace if any. Makes the medium text case insensitive and filter outs the malformed params.
 */
export const stringifyVals = (vals: any[]) =>
  vals.map((val) => (typeof val !== 'object' ? val : JSON.stringify(val))).join('');

/**
 * Sorts the keys of an object alphabetically.
 *
 * @param {Record<string, any>} obj - The object to sort.
 * @returns {Record<string, any>} The sorted object.
 */
const sortObject = (obj: Record<string, any>) => {
  const sortedEntries = Object.entries(obj).sort(([a], [b]) => a.localeCompare(b));
  return Object.fromEntries(sortedEntries);
};

/**
 * Sorts and stringifies the keys of query parameters.
 * Nested objects are also sorted.
 *
 * @param {Record<string, any>} queryParams - The query parameters to sort and stringify.
 * @returns {string} The JSON string representation of the sorted query parameters.
 */
export const sortAndStringifyQueryParams = (queryParams: Record<string, any>): string => {
  const sortedQueryParams = sortObject(queryParams);

  return JSON.stringify(sortedQueryParams, (key, value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return sortObject(value);
    }
    return value;
  });
};
